// Service for the PAEI Analysis screen (AI Agents → CAPI & PAEI Analysis).
//
// Everything here talks to the Python Process Optimization agent, the same origin
// the other /cms/* optimization endpoints use, with the Bearer token forwarded so
// CMS can filter the candidate pool server-side. Nothing in this file writes:
// the screen computes, it does not persist. Editing PAEI/CAPI profiles stays with
// paeiService.js / capiService.js against the Nest backend.
//
// The read of a process's step descriptions is a model call — 30-90s on a first
// run, ~2s afterwards from the stored reading — so the UI must always go through
// POST .../start + poll, never the synchronous POST.
const AGENT_BASE_URL =
  process.env.REACT_APP_PROCESS_OPT_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_BASE_URL ||
  "";

const BASE_PATH = "/cms/paei-analysis";

function buildHeaders(extra = {}) {
  let token = null;
  try {
    const raw = sessionStorage.getItem("auth");
    if (raw) token = JSON.parse(raw)?.accessToken || null;
  } catch (_) { }
  return {
    Accept: "application/json,*/*",
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
}

function fetchWithTimeout(url, options, timeoutMs) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  return fetch(url, { ...options, signal: controller.signal })
    .then((res) => { clearTimeout(timer); return res; })
    .catch((err) => {
      clearTimeout(timer);
      if (err.name === "AbortError") throw new Error(`Request timed out after ${timeoutMs / 1000}s`);
      throw err;
    });
}

// A 502 from this service means the agent could not reach CMS (or /paei is not
// deployed there). It is NOT an access denial, so it must not be routed through
// the RBAC error path — tag it so the page can say the right thing.
async function raiseForStatus(res) {
  const text = await res.text().catch(() => "");
  let message = text;
  try {
    const parsed = JSON.parse(text);
    message = parsed?.detail || parsed?.message || parsed?.error || text;
    if (Array.isArray(message)) message = message.join("; ");
  } catch (_) { /* not JSON — keep the raw text */ }

  const err = new Error(
    Number(res.status) === 502 ?
      "The optimization agent could not reach CMS (502). This is a connectivity problem, not an access denial — retry, or check that /paei is deployed."
    : message || `HTTP ${res.status}`,
  );
  err.status = res.status;
  err.isUpstreamUnreachable = Number(res.status) === 502;
  throw err;
}

async function getJson(path, timeoutMs = 30_000) {
  const res = await fetchWithTimeout(
    `${AGENT_BASE_URL}${path}`,
    { method: "GET", headers: buildHeaders() },
    timeoutMs,
  );
  if (!res.ok) await raiseForStatus(res);
  return res.json();
}

async function postJson(path, body, timeoutMs = 30_000) {
  const res = await fetchWithTimeout(
    `${AGENT_BASE_URL}${path}`,
    { method: "POST", headers: buildHeaders(), body: JSON.stringify(body || {}) },
    timeoutMs,
  );
  if (!res.ok) await raiseForStatus(res);
  return res.json();
}

/* ------------------------------------------------------------------------ */
/* Reference data                                                            */
/* ------------------------------------------------------------------------ */

// The five preset keys the agent accepts. The server rejects anything else with
// `ValueError: unknown goal preset '<x>'`, so this is both the fallback when the
// endpoint is unreachable and the guard that stops a wrapper field in the
// response from being sent as if it were a goal.
export const GOAL_PRESET_KEYS = ["balanced", "throughput", "control", "change", "cohesion"];

const GOAL_PRESET_COPY = {
  balanced: { label: "Balanced", description: "No dimension favoured — every role weighted evenly." },
  throughput: { label: "Throughput", description: "Leans Producer: getting the work itself done." },
  control: { label: "Control", description: "Leans Administrator: the checks actually happening." },
  change: { label: "Change", description: "Leans Entrepreneur: initiative and reshaping the work." },
  cohesion: { label: "Cohesion", description: "Leans Integrator: keeping the people on it aligned." },
};

export const DEFAULT_GOAL_PRESETS = GOAL_PRESET_KEYS.map((key) => ({
  key,
  ...GOAL_PRESET_COPY[key],
  weights: null,
}));

// The endpoint has been seen returning the presets nested under a container field
// rather than at the top level. Descend into a known container before treating an
// object as a `{ key: {...} }` map — otherwise the container's own name ("presets")
// becomes a chip, and picking it sends an invalid goal to the agent.
function unwrapGoalContainer(raw) {
  if (Array.isArray(raw)) return raw;
  if (!raw || typeof raw !== "object") return [];
  for (const field of ["goals", "presets", "goal_presets", "data", "items"]) {
    if (raw[field] != null) return unwrapGoalContainer(raw[field]);
  }
  return Object.entries(raw)
    .filter(([, v]) => v && typeof v === "object")
    .map(([key, v]) => ({ key, ...v }));
}

/** Goal presets for the chips. Always resolves to something selectable. */
export async function getPaeiGoals() {
  let list = [];
  try {
    list = unwrapGoalContainer(await getJson(`${BASE_PATH}/goals`));
  } catch (_) {
    return DEFAULT_GOAL_PRESETS;
  }

  const normalized = list
    .map((g) => {
      const key = g?.key || g?.goal || g?.name || null;
      // Only keys the agent will actually accept — anything else is a wrapper or
      // a metadata field that happened to hold an object.
      if (!key || !GOAL_PRESET_KEYS.includes(String(key))) return null;
      return {
        key: String(key),
        label: g.label || g.title || GOAL_PRESET_COPY[key]?.label || key,
        description: g.description || g.blurb || GOAL_PRESET_COPY[key]?.description || null,
        weights: g.weights || (
          ["p", "a", "e", "i"].every((k) => g[k] != null) ?
            { p: g.p, a: g.a, e: g.e, i: g.i }
          : null
        ),
      };
    })
    .filter(Boolean);

  return normalized.length > 0 ? normalized : DEFAULT_GOAL_PRESETS;
}

// The nine-row demand-shift reference. Rendered in full on the Shift reference
// tab so Empower's relocation case stays documented even on runs that never
// fire it.
export async function getPaeiHeuristics() {
  const raw = await getJson(`${BASE_PATH}/heuristics`);
  const list =
    Array.isArray(raw) ? raw
    : Array.isArray(raw?.heuristics) ? raw.heuristics
    : raw && typeof raw === "object" ?
      Object.entries(raw).map(([k, v]) => ({ heuristic: k, ...(v && typeof v === "object" ? v : {}) }))
    : [];

  return list
    .map((h) => ({
      heuristic: h?.heuristic || h?.name || h?.key || "",
      headline: h?.headline || "",
      detail: h?.detail || "",
      delta: h?.delta || null,
      relocates: h?.relocates || null,
    }))
    .filter((h) => h.heuristic);
}

/**
 * Pre-flight coverage for a process — how much of the candidate pool has a PAEI
 * profile *that this caller may see*, plus a `reading` block saying what the run
 * will cost in time. Call it on process select, so both the thin-pool warning
 * and the cold-run expectation land before anyone presses Run.
 *
 * `reading`: { steps_total, steps_cached, steps_to_read, fully_cached, estimate }
 *
 * `can_run` is always true on the server. Never use this to disable Run.
 */
export async function getPaeiCoverage(processId) {
  if (!processId) throw new Error("processId is required");
  return getJson(`${BASE_PATH}/coverage/${encodeURIComponent(processId)}`);
}

/* ------------------------------------------------------------------------ */
/* Analysis run (async job + poll)                                           */
/* ------------------------------------------------------------------------ */

// The job URL the server hands back may be absolute or a path. Normalize to
// something fetchWithTimeout can prefix, and fall back to the documented route.
function resolvePollPath(pollUrl, jobId) {
  if (typeof pollUrl === "string" && pollUrl) {
    if (/^https?:\/\//i.test(pollUrl)) return pollUrl.replace(AGENT_BASE_URL, "");
    return pollUrl.startsWith("/") ? pollUrl : `/${pollUrl}`;
  }
  return `${BASE_PATH}/jobs/${encodeURIComponent(jobId)}`;
}

/**
 * Run a PAEI analysis end-to-end: start the job, then poll until it resolves.
 *
 * A cold run reads every step description with the model — 35-65s measured — and
 * only steps whose TEXT changed are ever re-read, so the slow case happens once
 * per process, not once per run. Poll every 2s.
 *
 * The 5-minute cap is a backstop for a stuck provider, not an expected wait: the
 * server bounds its own model time (PAEI_LLM_BUDGET_SECONDS, 240s) and returns a
 * keyword-fallback result rather than hanging, so a job still running at the cap
 * has genuinely failed.
 *
 * @param {number} processId
 * @param {object} options
 * @param {string|{p:number,a:number,e:number,i:number}} [options.goal] preset key or explicit weights
 * @param {boolean} [options.useLlm] false = keyword fallback
 * @param {number}  [options.samples] >1 reads N times and takes the per-role median
 * @param {boolean} [options.refreshReading] ignore the stored reading and read again
 * @param {number}  [options.episodes]
 * @param {(attempt:number, job:object)=>void} [options.onPoll] progress hook, for the elapsed counter
 * @param {(detail:string)=>void} [options.onStage] the job's own `stage_detail`,
 *   to be rendered verbatim — a run that sits silent for 40s reads as a hang
 */
export async function runPaeiAnalysis(processId, options = {}) {
  if (!processId) throw new Error("processId is required");

  const body = {};
  if (options.goal != null) body.goal = options.goal;
  if (options.useLlm != null) body.use_llm = options.useLlm;
  if (options.samples != null) body.samples = options.samples;
  if (options.refreshReading != null) body.refresh_reading = options.refreshReading;
  if (options.episodes != null) body.episodes = options.episodes;

  const started = await postJson(`${BASE_PATH}/${encodeURIComponent(processId)}/start`, body);
  const jobId = started?.job_id;
  const pollPath = resolvePollPath(started?.poll_url, jobId);
  if (!jobId && !started?.poll_url) {
    throw new Error("The agent did not return a job id to poll.");
  }

  const MAX_POLLS = 150; // 150 × 2s = 5 minutes
  for (let attempt = 1; attempt <= MAX_POLLS; attempt++) {
    await new Promise((r) => setTimeout(r, 2000));
    const job = await getJson(pollPath);
    if (typeof options.onPoll === "function") options.onPoll(attempt, job);
    const detail = job?.stage_detail || job?.stage || null;
    if (detail && typeof options.onStage === "function") options.onStage(detail);
    if (job?.status === "done") return job.result;
    if (job?.status === "error") throw new Error(job.error || "PAEI analysis failed on the server");
  }
  throw new Error("PAEI analysis timed out after 5 minutes. Please try again.");
}
